import { ref, readonly, computed, onMounted, onUnmounted } from 'vue';
import { useLogger } from '@/composables/useLogger';

export interface ViewportState {
  width: number;
  height: number;
  vh: number; // 1vh in pixels (innerHeight / 100)
  vw: number; // 1vw in pixels (innerWidth / 100)
  aspectRatio: number;
  isPortrait: boolean;
  isLandscape: boolean;
}

/**
 * useViewport composable
 *
 * Provides reactive viewport dimensions and CSS custom property updates
 * for handling mobile browser dynamic toolbar behavior (iOS Safari, Android Chrome).
 *
 * Features:
 * - Sets --vh CSS variable for JS polyfill fallback (100dvh → 100svh → calc(var(--vh) * 100) → 100vh)
 * - Sets --vw CSS variable for consistent viewport width calculations
 * - Reactive state for component consumption
 * - Debounced resize handling for performance
 * - Orientation change support
 *
 * @example
 * ```ts
 * const { viewport, vh } = useViewport();
 * // Use in template: <div :style="{ height: `${vh * 100}px` }">
 * // Or use CSS: height: calc(var(--vh) * 100);
 * ```
 */
export function useViewport() {
  const { log, debug } = useLogger();

  // Reactive state
  const viewport = ref<ViewportState>({
    width: 0,
    height: 0,
    vh: 0,
    vw: 0,
    aspectRatio: 0,
    isPortrait: true,
    isLandscape: false
  });

  // Debounce timer for resize events
  let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  const RESIZE_DEBOUNCE_MS = 100;

  /**
   * Updates CSS custom properties and reactive state
   */
  const updateViewport = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const vh = height * 0.01;
    const vw = width * 0.01;

    // Update CSS custom properties (used by CSS fallbacks)
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--vw', `${vw}px`);

    // Update reactive state
    viewport.value = {
      width,
      height,
      vh,
      vw,
      aspectRatio: width / height,
      isPortrait: height >= width,
      isLandscape: width > height
    };

    debug('useViewport', 'viewport-updated', {
      width,
      height,
      vh: `${vh.toFixed(2)}px`,
      vw: `${vw.toFixed(2)}px`,
      isPortrait: viewport.value.isPortrait
    });
  };

  /**
   * Debounced resize handler for performance
   */
  const handleResize = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(() => {
      updateViewport();
    }, RESIZE_DEBOUNCE_MS);
  };

  /**
   * Handle orientation changes with delay for transition completion
   */
  const handleOrientationChange = () => {
    log('useViewport', 'orientation-change-detected', {
      angle: window.screen.orientation?.angle,
      type: window.screen.orientation?.type
    });

    // Delay update to allow orientation transition to complete
    setTimeout(() => {
      updateViewport();
    }, 300);
  };

  /**
   * Handle visual viewport changes (iOS Safari toolbar show/hide)
   */
  const handleVisualViewportChange = () => {
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      debug('useViewport', 'visual-viewport-changed', {
        width: visualViewport.width,
        height: visualViewport.height,
        offsetTop: visualViewport.offsetTop,
        pageTop: visualViewport.pageTop
      });

      // Update viewport based on visual viewport for more accuracy
      const height = visualViewport.height;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
  };

  /**
   * Initialize viewport on mount
   */
  const initialize = () => {
    log('useViewport', 'initializing', {
      userAgent: navigator.userAgent,
      visualViewportSupported: 'visualViewport' in window
    });

    updateViewport();

    // Listen for resize events (debounced)
    window.addEventListener('resize', handleResize, { passive: true });

    // Listen for orientation changes
    window.addEventListener('orientationchange', handleOrientationChange);

    // Listen for visual viewport changes (iOS Safari specific)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange, { passive: true });
    }

    debug('useViewport', 'initialized', {
      initialWidth: viewport.value.width,
      initialHeight: viewport.value.height
    });
  };

  /**
   * Cleanup on unmount
   */
  const cleanup = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleOrientationChange);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
    }

    log('useViewport', 'cleanup-completed');
  };

  // Auto-initialize on mount
  onMounted(() => {
    initialize();
  });

  // Auto-cleanup on unmount
  onUnmounted(() => {
    cleanup();
  });

  // Computed refs for primitive values
  const vh = computed(() => viewport.value.vh);
  const vw = computed(() => viewport.value.vw);

  return {
    /**
     * Readonly reactive viewport state object
     */
    viewport: readonly(viewport),

    /**
     * Computed ref for vh value in pixels (1% of viewport height)
     * Use in templates: :style="{ height: `${vh.value * 100}px` }"
     */
    vh,

    /**
     * Computed ref for vw value in pixels (1% of viewport width)
     */
    vw,

    /**
     * Manual update function (if needed)
     */
    updateViewport,

    /**
     * Manual cleanup function (if needed outside component lifecycle)
     */
    cleanup
  };
}
